import type { CardConfig, ReactionType, DataValues } from "../types/narrative";
import { replacePlaceholders } from "./placeholders";

export interface ShareableCard {
  groupId: string;
  sectionLabel?: string;
  cardConfig: CardConfig;
  reaction?: ReactionType | null;
}

// Reaction emoji mapping
const reactionEmojis: Record<ReactionType, string> = {
  positive: "👍",
  negative: "👎",
  question: "❓",
};

/**
 * Generate email body text from a collection of cards
 * @param cards - Array of shareable cards
 * @param customerName - Customer name for personalization
 * @param data - Data values for placeholder replacement
 * @returns Formatted email body text
 */
export function generateEmailBody(
  cards: ShareableCard[],
  customerName: string,
  data: DataValues,
): string {
  if (cards.length === 0) {
    return "No insights to share.";
  }

  // Email header
  let emailBody = `Hi,\n\n`;
  emailBody += `I wanted to share ${cards.length} key insight${cards.length === 1 ? "" : "s"} from ${customerName}'s recent report:\n\n`;

  // Group cards by section
  const cardsBySection = new Map<string, ShareableCard[]>();
  cards.forEach((card) => {
    const sectionLabel = card.sectionLabel || "Insights";
    if (!cardsBySection.has(sectionLabel)) {
      cardsBySection.set(sectionLabel, []);
    }
    cardsBySection.get(sectionLabel)!.push(card);
  });

  // Format each section
  cardsBySection.forEach((sectionCards, sectionLabel) => {
    emailBody += `━━━━━━━━━━━━━━━━━━━━━━\n`;
    emailBody += `${sectionLabel.toUpperCase()}\n`;
    emailBody += `━━━━━━━━━━━━━━━━━━━━━━\n\n`;

    sectionCards.forEach((card) => {
      const title = replacePlaceholders(card.cardConfig.title, data);
      const body = replacePlaceholders(card.cardConfig.body, data);
      const metric = card.cardConfig.metric
        ? replacePlaceholders(card.cardConfig.metric, data)
        : undefined;

      // Add reaction emoji if present
      const reactionEmoji = card.reaction
        ? `${reactionEmojis[card.reaction]} `
        : "";

      emailBody += `${reactionEmoji}${title}\n`;
      if (metric && card.cardConfig.style === "metric") {
        emailBody += `${metric}\n`;
      }
      emailBody += `${body}\n\n`;
    });
  });

  // Email footer
  emailBody += `━━━━━━━━━━━━━━━━━━━━━━\n\n`;
  emailBody += `Questions about any of these insights? I'd love to discuss how we can build on this momentum together.\n\n`;
  emailBody += `Best regards`;

  return emailBody;
}

/**
 * Generate mailto link with pre-filled subject and body
 * @param to - Recipient email address (optional, empty for user to fill)
 * @param subject - Email subject line
 * @param body - Email body text
 * @returns mailto: URL
 */
export function generateMailtoLink(
  to: string = "",
  subject: string,
  body: string,
): string {
  const params = new URLSearchParams();
  if (to) params.append("to", to);
  params.append("subject", subject);
  params.append("body", body);

  return `mailto:${to}?${params.toString()}`;
}

/**
 * Share cards via email (opens mailto link or native share)
 * @param cards - Array of shareable cards
 * @param customerName - Customer name
 * @param data - Data values for placeholder replacement
 * @param recipientType - Type of recipient (csm or peers)
 * @param contactEmail - Contact email address from narrative metadata (optional)
 * @returns Promise that resolves when share attempt completes
 */
export async function shareCardsViaEmail(
  cards: ShareableCard[],
  customerName: string,
  data: DataValues,
  recipientType: "csm" | "peers" = "csm",
  contactEmail?: string,
): Promise<void> {
  const subject =
    recipientType === "csm"
      ? `Questions about ${customerName}'s Insights`
      : `${customerName} Insights Summary`;

  const body = generateEmailBody(cards, customerName, data);

  // Try Web Share API first (better mobile experience)
  if (navigator.share) {
    try {
      await navigator.share({
        title: subject,
        text: body,
      });
      return;
    } catch (error: unknown) {
      if (error instanceof DOMException && error.name === "AbortError") {
        return; // User cancelled, don't fallback
      }
      // Fall through to mailto
    }
  }

  // Fallback to mailto link
  const to = recipientType === "csm" && contactEmail ? contactEmail : "";
  const mailtoLink = generateMailtoLink(to, subject, body);

  // Open mailto link
  window.location.href = mailtoLink;
}

/**
 * Generate a concise summary text for sharing (single insight)
 * @param card - Single card to share
 * @param customerName - Customer name
 * @param data - Data values
 * @returns Formatted share text
 */
export function generateInsightShareText(
  card: ShareableCard,
  customerName: string,
  data: DataValues,
): string {
  const title = replacePlaceholders(card.cardConfig.title, data);
  const body = replacePlaceholders(card.cardConfig.body, data);
  const metric = card.cardConfig.metric
    ? replacePlaceholders(card.cardConfig.metric, data)
    : undefined;

  let shareText = `${title}\n\n`;
  if (metric && card.cardConfig.style === "metric") {
    shareText += `${metric}\n\n`;
  }
  shareText += `${body}\n\n`;
  shareText += `— From ${customerName}'s insights`;

  return shareText;
}

/**
 * Copy text to clipboard
 * @param text - Text to copy
 * @returns Promise that resolves when copy completes
 */
export async function copyToClipboard(text: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
  } catch (error: unknown) {
    console.warn("Navigator clipboard copy failed, using fallback.", error);
    // Fallback for older browsers
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand("copy");
    document.body.removeChild(textarea);
  }
}
